import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  HttpMethod,
  ProductOffering,
  SLHeaders,
  useConfig,
  useSession,
} from '@travelwin/core';
import { formatISO } from 'date-fns';
import { createSearchParams } from 'react-router';
import invariant from 'tiny-invariant';

import { useCurrency } from '../../context/Currency';
import { FindMyPlanResult } from '../../pages/FindMyPlan/components/FindMyPlanStep';
import { getApiUrl, request, requestOrRedirect } from '../helpers/requests';

const PRODUCT_QUERY_KEY = 'product';

const getProductByIdQueryKey = (productId?: string, currency?: string) =>
  [PRODUCT_QUERY_KEY, { productId, currency }] as const;

const getActiveProductQueryKey = ({
  sku,
  definedCurrency,
}: {
  sku?: string;
  definedCurrency?: string;
}) => [PRODUCT_QUERY_KEY, { sku, definedCurrency }] as const;

export const setProductQueryData = (
  queryClient: QueryClient,
  product: ProductOffering,
) => {
  queryClient.setQueryData(getProductByIdQueryKey(product.id), product);
  queryClient.setQueryData(
    getActiveProductQueryKey({
      sku: product.sku,
    }),
    product,
  );
};

export const getProductOfferings = ({
  sessionId,
  country_id,
  country_ids,
  travel_date,
  ids,
  currency,
  locale,
  country_slug,
  visitorOrigin,
  operatorId,
  minTravelDurationInDays,
  maxTravelDurationInDays,
}: {
  sessionId: string;
  country_id?: string;
  country_ids?: Array<string>;
  travel_date?: string; // format yyyy-mm-dd e.g. 2018-04-19
  ids?: string[];
  currency?: string;
  locale?: string;
  country_slug?: string;
  visitorOrigin?: string;
  operatorId?: string;
  minTravelDurationInDays?: number;
  maxTravelDurationInDays?: number;
}): Promise<ProductOffering[]> => {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      [SLHeaders.SESSION_ID]: sessionId,
      ...(currency ? { [SLHeaders.CURRENCY]: currency } : {}),
      ...(locale ? { [SLHeaders.LOCALE]: locale } : {}),
      ...(visitorOrigin ? { [SLHeaders.VISITOR_ORIGIN]: visitorOrigin } : {}),
    },
  };

  const paramsObj: Record<string, string | string[]> = {
    country_id: country_id ?? [],
    travel_date: travel_date ?? [],
    ids: ids && ids.length > 0 ? ids.join(',') : [],
    country_slug: country_slug ?? [],
  };

  if (country_ids && country_ids.length > 0) {
    paramsObj.country_ids = country_ids.join(',');
  }

  if (operatorId) {
    paramsObj.mvnoId = operatorId;
  }

  if (minTravelDurationInDays !== undefined) {
    paramsObj.min_travel_duration_in_days = minTravelDurationInDays.toString();
  }

  if (maxTravelDurationInDays !== undefined) {
    paramsObj.max_travel_duration_in_days = maxTravelDurationInDays.toString();
  }

  const params = createSearchParams(paramsObj);
  const path = `${getApiUrl()}ecommerce/product_offerings?${params}`;

  return request(path, options);
};

export const getPlanDetailsPaths = (
  sessionId: string,
  retailerId = '100',
): Promise<string[]> => {
  const options = {
    method: HttpMethod.GET,
    headers: { [SLHeaders.SESSION_ID]: sessionId },
  };

  const params = createSearchParams({ retailerId });

  const path = `${getApiUrl()}ecommerce/product_offerings/public/links?${params}`;

  return request(path, options);
};

export const useProductsQuery = ({
  country_id,
  country_ids,
  mvno_id,
  minTravelDurationInDays,
  maxTravelDurationInDays,
  options,
}: {
  country_id?: string;
  country_ids?: Array<string>;
  mvno_id?: string;
  minTravelDurationInDays?: number;
  maxTravelDurationInDays?: number;
  options?: { enabled?: boolean };
}) => {
  const queryClient = useQueryClient();
  const { currency } = useCurrency();
  const { sessionId, visitorOrigin } = useSession();
  const { PAYMENT_DEFAULT_CURRENCY } = useConfig();
  const definedCurrency = currency ?? PAYMENT_DEFAULT_CURRENCY;

  return useQuery({
    queryKey: [
      PRODUCT_QUERY_KEY,
      'list',
      {
        country_id,
        country_ids,
        definedCurrency,
        mvno_id,
        minTravelDurationInDays,
        maxTravelDurationInDays,
      },
    ],
    enabled: options?.enabled,
    queryFn: async () => {
      const products = await getProductOfferings({
        sessionId,
        visitorOrigin,
        country_id,
        country_ids,
        travel_date: formatISO(new Date(), { representation: 'date' }),
        currency: definedCurrency,
        operatorId: mvno_id,
        minTravelDurationInDays,
        maxTravelDurationInDays,
      });
      products.forEach((product) => {
        setProductQueryData(queryClient, product);
      });
      return products;
    },
  });
};

export const useProductByIdQuery = (productId?: string, currency?: string) => {
  const { sessionId, visitorOrigin } = useSession();

  return useQuery({
    queryKey: getProductByIdQueryKey(productId, currency),
    enabled: Boolean(productId),
    queryFn: () => {
      invariant(productId);

      const options: RequestInit = {
        method: 'GET',
        headers: {
          ...(currency ? { [SLHeaders.CURRENCY]: currency } : {}),
          [SLHeaders.SESSION_ID]: sessionId,
          ...(visitorOrigin
            ? { [SLHeaders.VISITOR_ORIGIN]: visitorOrigin }
            : {}),
        },
      };

      return request<ProductOffering>(
        `${getApiUrl()}ecommerce/product_offerings/public/${productId}`,
        options,
      );
    },
  });
};

export const getProductByReadablePathServer = async ({
  sessionId,
  product,
  mvno,
  sku,
  currency,
  locale,
  visitorOrigin,
}: {
  sessionId: string;
  product?: string;
  mvno?: string;
  sku?: string;
  currency?: string;
  locale?: string;
  visitorOrigin?: string;
}) => {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      [SLHeaders.SESSION_ID]: sessionId,
      ...(currency ? { [SLHeaders.CURRENCY]: currency } : {}),
      ...(locale ? { [SLHeaders.LOCALE]: locale } : {}),
      ...(visitorOrigin ? { [SLHeaders.VISITOR_ORIGIN]: visitorOrigin } : {}),
    },
  };

  const path = `${getApiUrl()}ecommerce/product_offerings/public/${mvno}/${product}/${sku}`;

  // The `requestOrRedirect` function has dual behavior:
  // - If the response status is 301, it returns an object with a `redirectUrl`.
  // - Otherwise, it returns the standard response body.
  const response = await requestOrRedirect(path, options);

  if (response?.status === 301 && response.redirectUrl) {
    return response;
  } else {
    return response?.body;
  }
};

export const getProductByReadablePath = ({
  sessionId,
  product,
  mvno,
  sku,
  currency,
  locale,
  visitorOrigin,
}: {
  sessionId: string;
  product?: string;
  mvno?: string;
  sku?: string;
  currency?: string;
  locale?: string;
  visitorOrigin?: string;
}): Promise<ProductOffering> => {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      [SLHeaders.SESSION_ID]: sessionId,
      ...(currency ? { [SLHeaders.CURRENCY]: currency } : {}),
      ...(locale ? { [SLHeaders.LOCALE]: locale } : {}),
      ...(visitorOrigin ? { [SLHeaders.VISITOR_ORIGIN]: visitorOrigin } : {}),
    },
  };

  const path = `${getApiUrl()}ecommerce/product_offerings/public/${mvno}/${product}/${sku}`;

  return request(path, options);
};

export const useProductByReadablePath = ({
  product,
  mvno,
  sku,
  currency,
  visitorOrigin,
}: {
  product?: string;
  mvno?: string;
  sku?: string;
  currency?: string;
  visitorOrigin?: string;
}) => {
  const { sessionId } = useSession();

  const path = `${mvno}/${product}/${sku}`;

  return useQuery({
    queryKey: getProductByIdQueryKey(path, currency),
    enabled: Boolean(mvno && product && sku),
    queryFn: () => {
      return getProductByReadablePath({
        sessionId,
        product,
        mvno,
        sku,
        currency,
        visitorOrigin,
      });
    },
  });
};

export const useActiveProductQuery = ({
  countryId,
  sku,
}: {
  countryId?: string;
  sku?: string;
}) => {
  const { sessionId } = useSession();

  const { currency } = useCurrency();
  const { PAYMENT_DEFAULT_CURRENCY } = useConfig();
  const definedCurrency = currency ?? PAYMENT_DEFAULT_CURRENCY;

  return useQuery({
    queryKey: getActiveProductQueryKey({ sku, definedCurrency }),
    enabled: Boolean(countryId) && Boolean(sku),
    queryFn: () => {
      invariant(countryId);
      invariant(sku);

      const options: RequestInit = {
        method: 'GET',
        headers: {
          [SLHeaders.SESSION_ID]: sessionId,
          ...(currency ? { [SLHeaders.CURRENCY]: currency } : {}),
        },
      };

      const searchParams = new URLSearchParams({
        countryId,
        sku,
      });

      return request<ProductOffering>(
        `${getApiUrl()}ecommerce/product_offerings/public/active?${searchParams}`,
        options,
      );
    },
  });
};

export const useProductQuery = ({
  productId,
  countryId,
  sku,
  planName,
  operator,
  visitorOrigin,
}: {
  productId?: string;
  countryId?: string;
  sku?: string;
  planName?: string;
  operator?: string;
  visitorOrigin?: string;
}) => {
  const { currency } = useCurrency();
  const { PAYMENT_DEFAULT_CURRENCY } = useConfig();
  const definedCurrency = currency ?? PAYMENT_DEFAULT_CURRENCY;
  const productByIdQuery = useProductByIdQuery(productId, definedCurrency);
  const activeProductQuery = useActiveProductQuery({ countryId, sku });
  const productByReadablePath = useProductByReadablePath({
    product: planName,
    mvno: operator,
    sku,
    currency: definedCurrency,
    visitorOrigin,
  });

  if (productId) {
    return productByIdQuery;
  } else if (countryId && sku) {
    return activeProductQuery;
  } else if (planName && operator && sku) {
    return productByReadablePath;
  } else {
    throw new Error(
      'Either productId or countryId and sku need must be provided.',
    );
  }
};

export const findMyPlan = (
  sessionId: string,
  data: FindMyPlanResult,
  currency?: string,
): Promise<ProductOffering[]> => {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      [SLHeaders.SESSION_ID]: sessionId,
      ...(currency ? { [SLHeaders.CURRENCY]: currency } : {}),
    },
  };

  const query = new URLSearchParams(data);

  const path = `${getApiUrl()}ecommerce/product_offerings?${query.toString()}`;

  return request(path, options);
};

export const useFindMyPlanMutation = (delay = 0) => {
  const queryClient = useQueryClient();
  const { sessionId } = useSession();
  const { currency } = useCurrency();
  const { PAYMENT_DEFAULT_CURRENCY } = useConfig();
  const definedCurrency = currency ?? PAYMENT_DEFAULT_CURRENCY;

  return useMutation({
    mutationFn: async (data: FindMyPlanResult) => {
      const findMyPlanPromise = (async () => {
        const products = await findMyPlan(sessionId, data, definedCurrency);

        products.forEach((product) => {
          setProductQueryData(queryClient, product);
        });
        return products;
      })();

      const waitPromise = new Promise((r) => setTimeout(r, delay));

      const [products] = await Promise.all([findMyPlanPromise, waitPromise]);

      return products;
    },
  });
};
